import { Injectable, computed, inject, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  EmployeeClient,
  EmployeeV3ForCardDto,
  EmployeeV3PositionItemDto,
} from '@data-access/bulk-operations-api';
import { take } from 'rxjs';

import { ToasterService } from '#shared/components/toaster/toaster.service';

import { SelectedEmployeeService } from '../employee/employee-card/services/selected-employee.service';

export type CopiedPanelKeys =
  | 'personalDetails'
  | 'employmentCondition'
  | 'salary'
  | 'manager'
  | 'costUnits'
  | 'tags'
  | 'nav';

@Injectable({
  providedIn: 'root',
})
export class EmployeeHiringService {
  private readonly employeeClient = inject(EmployeeClient);
  private readonly selectedEmployeeService = inject(SelectedEmployeeService);
  private readonly toasterService = inject(ToasterService);
  private readonly _employeeSignal = signal<EmployeeV3ForCardDto>(undefined);
  private readonly _positionSignal = signal<EmployeeV3PositionItemDto>(undefined);
  private readonly _flowTypeSignal = signal<
    'rehire-copy' | 'rehire-new' | 'create' | 'edit-draft' | 'change-employment-type'
  >(null);
  private readonly _previousPositionSignal = signal<EmployeeV3PositionItemDto>(undefined);
  private readonly _templateLoadingSignal = signal<boolean>(false);
  private readonly _copiedPanelKeysSignal = signal<CopiedPanelKeys[]>([]);

  public readonly employeeSignal = this._employeeSignal.asReadonly();
  public readonly positionSignal = this._positionSignal.asReadonly();
  public readonly flowTypeSignal = this._flowTypeSignal.asReadonly();
  public readonly previousPositionSignal = this._previousPositionSignal.asReadonly();
  public readonly templateLoadingSignal = this._templateLoadingSignal.asReadonly();
  public readonly copiedPanelKeysSignal = this._copiedPanelKeysSignal.asReadonly();

  public readonly isRehiringSignal = computed(() =>
    ['rehire-copy', 'rehire-new'].includes(this._flowTypeSignal()),
  );
  public readonly isEditDraftSignal = computed(() => this._flowTypeSignal() === 'edit-draft');
  public readonly isChangeEmploymentTypeSignal = computed(
    () => this._flowTypeSignal() === 'change-employment-type',
  );
  public readonly isRehireOrChangeTypeSignal = computed(
    () => this.isRehiringSignal() || this.isChangeEmploymentTypeSignal(),
  );
  public readonly isInHiringFlowSignal = computed(() => this._flowTypeSignal() !== null);

  public readonly positionStartDateFormControlSignal =
    signal<FormControl<Date | undefined>>(undefined);

  public setPositionStartDateFormControl(formControl: FormControl<Date>): void {
    this.positionStartDateFormControlSignal.set(formControl);
  }

  public setEmployeeId(employeeId: string): void {
    this._flowTypeSignal.set(undefined);
    this.handleEmployeeSearch(employeeId);
  }

  public cleanupRehire(): void {
    this._employeeSignal.set(undefined);
    this._positionSignal.set(undefined);
    this._flowTypeSignal.set(undefined);
    this._previousPositionSignal.set(undefined);
    this.positionStartDateFormControlSignal.set(undefined);
    this._copiedPanelKeysSignal.set([]);
  }

  private handleEmployeeSearch(employeeId: string): void {
    this.employeeClient.getEmployee(employeeId).subscribe({
      next: (resp) => {
        if (resp.employee.isDraft) {
          this.editDraft(resp.employee);
        } else {
          this._employeeSignal.set(resp.employee);
        }
      },
    });
  }

  public editDraft(employeeV3ForCardDto: EmployeeV3ForCardDto) {
    this._employeeSignal.set(employeeV3ForCardDto);

    const positionTimeline = employeeV3ForCardDto.positionTimeline;
    this._positionSignal.set(
      positionTimeline.length > 0 ? positionTimeline[positionTimeline.length - 1] : undefined,
    );

    this._flowTypeSignal.set('edit-draft');
  }

  public rehireWithCopyFromLastPosition(selectedEmployee: EmployeeV3ForCardDto | null) {
    if (selectedEmployee) {
      this._employeeSignal.set(selectedEmployee);
    }

    // Employee in modal is set when executing search
    const positionTimeline = this._employeeSignal().positionTimeline;
    this._positionSignal.set(
      positionTimeline.length > 0 ? positionTimeline[positionTimeline.length - 1] : undefined,
    );

    this._flowTypeSignal.set('rehire-copy');
  }

  public rehireWithBlankPosition(selectedEmployee: EmployeeV3ForCardDto | null) {
    if (selectedEmployee) {
      this._employeeSignal.set(selectedEmployee);
      this._positionSignal.set(undefined);
    }

    this._flowTypeSignal.set('rehire-new');
  }

  public changeEmploymentType(): void {
    const employee = this.selectedEmployeeService.employeeSignal();
    const currentPosition = this.selectedEmployeeService.positionSignal();
    if (employee && currentPosition) {
      this._employeeSignal.set(employee);
      this._previousPositionSignal.set(currentPosition);
      this._positionSignal.set({
        ...currentPosition,
        value: {
          ...currentPosition.value,
          employmentType: undefined,
        },
      } as EmployeeV3PositionItemDto);
    }

    this._flowTypeSignal.set('change-employment-type');
  }

  public setTemplateEmployee(employeeId: string | null): void {
    if (!employeeId) {
      this._positionSignal.set(undefined);
      this._flowTypeSignal.set(null);
      this._copiedPanelKeysSignal.set([]);
      return;
    }

    this._templateLoadingSignal.set(true);
    this._flowTypeSignal.set('create');

    this.employeeClient
      .getEmployee(employeeId)
      .pipe(take(1))
      .subscribe({
        next: (resp) => {
          const templateEmployee = resp.employee;
          const lastPosition =
            templateEmployee.positionTimeline[templateEmployee.positionTimeline.length - 1];

          lastPosition.from = undefined;
          lastPosition.to = undefined;
          lastPosition.value.layoffTimeline = undefined;
          lastPosition.value.endReason = undefined;
          this._positionSignal.set(lastPosition);

          this._copiedPanelKeysSignal.set([
            'personalDetails',
            'employmentCondition',
            'salary',
            'manager',
            'costUnits',
            'tags',
            'nav',
          ]);

          this._templateLoadingSignal.set(false);
        },
        error: () => {
          this._templateLoadingSignal.set(false);
          this._positionSignal.set(undefined);
          this._flowTypeSignal.set(null);
          this._copiedPanelKeysSignal.set([]);

          // Show error to user
          this.toasterService.showError(
            $localize`Failed to load employee template. Please try again.`,
          );
        },
      });
  }
}
